// OWMap BSL1 — Brew Panel
// Main Brew hub: credit arc, A/B mode cards, history strip.
// Rendered as a full-screen overlay from app.jsx.
// Log prefix: [BrewPanel]

const { useState: useSt, useEffect: useEff, useCallback: useCb, useRef: useRf, useMemo: useMm } = React;

// ── Brew品牌图标 (坩埚轮廓, 24×24) ───────────────────────────────────────────
function BrewIcon({ size = 24, color = '#c8973a', active = false }) {
  const c = active ? '#f5c842' : color;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Flask body */}
      <path
        d="M9 3 L9 9 L5 17 C4.4 18.1 5.2 19.5 6.5 19.5 L17.5 19.5 C18.8 19.5 19.6 18.1 19 17 L15 9 L15 3"
        stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
      />
      {/* Flask mouth bar */}
      <path d="M7.5 3 L16.5 3" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
      {/* Liquid level — subtle inner line */}
      <path
        d="M7 14 Q9 12.5 12 13.5 Q15 14.5 17 13"
        stroke={c} strokeWidth="0.8" strokeLinecap="round" opacity={active ? 1 : 0.5}
      />
    </svg>
  );
}

// ── 月相点数弧 ─────────────────────────────────────────────────────────────────
// 180° half-circle arc, bottom-open. Filled = spent, empty = remaining.
function CreditsArc({ balance, monthly, tier, daysUntilRefresh }) {
  const total = monthly || 1;
  const used  = Math.max(0, total - balance);
  const pct   = Math.min(1, used / total);

  // SVG arc: semicircle, 160px wide. Center (80,80), R=66
  const R   = 66;
  const CX  = 80;
  const CY  = 80;
  const startAngle = Math.PI;      // left = 180°
  const endAngle   = 2 * Math.PI;  // right = 360°
  const span = endAngle - startAngle; // π

  function polar(a) {
    return {
      x: CX + R * Math.cos(a),
      y: CY + R * Math.sin(a),
    };
  }

  const arcStart   = polar(startAngle);
  const arcEnd     = polar(endAngle);
  const fillEnd    = polar(startAngle + span * pct);
  const isFullUsed = pct >= 0.995;

  // Background track arc (full semicircle)
  const trackD = `M ${arcStart.x} ${arcStart.y} A ${R} ${R} 0 0 1 ${arcEnd.x} ${arcEnd.y}`;
  // Filled arc (spent portion)
  const fillD  = isFullUsed
    ? trackD
    : pct < 0.005
      ? ''
      : `M ${arcStart.x} ${arcStart.y} A ${R} ${R} 0 ${pct > 0.5 ? 1 : 0} 1 ${fillEnd.x} ${fillEnd.y}`;

  // Tier color
  const tierColor = tier === 'P2' || tier === 'X' ? '#f5c842' : '#c8973a';

  return (
    <div className="brew-credits-arc" aria-label={`点数余额 ${balance} / ${total}`}>
      <svg viewBox="0 0 160 90" width="160" height="90" className="arc-svg">
        {/* Track */}
        <path d={trackD} fill="none" stroke="rgba(200,151,58,0.15)" strokeWidth="8" strokeLinecap="round" />
        {/* Fill */}
        {fillD && (
          <path d={fillD} fill="none" stroke={tierColor} strokeWidth="8"
            strokeLinecap="round" className="arc-fill" />
        )}
        {/* Remaining label */}
        <text x={CX} y={CY - 8} textAnchor="middle" className="arc-balance"
          fill={tierColor}>{balance}</text>
        <text x={CX} y={CY + 12} textAnchor="middle" className="arc-total"
          fill="rgba(232,232,240,0.38)">/ {total} ⬡</text>
      </svg>
      {daysUntilRefresh !== null && (
        <div className="arc-refresh">
          下次补充：{daysUntilRefresh === 0 ? '今天' : `${daysUntilRefresh} 天后`}
        </div>
      )}
    </div>
  );
}

// ── BrewHistoryCard ────────────────────────────────────────────────────────────
function BrewHistoryCard({ item, onPlay }) {
  const statusLabel = {
    approved:    { text: '✓ 已通过',   cls: 'bhs-approved'  },
    rejected:    { text: '✗ 未通过',   cls: 'bhs-rejected'  },
    failed:      { text: '！失败',      cls: 'bhs-failed'    },
    review:      { text: '⧖ 审核中',   cls: 'bhs-review'    },
    generating:  { text: '◌ 生成中',   cls: 'bhs-pending'   },
    discovering: { text: '◌ 发现中',   cls: 'bhs-pending'   },
    queued:      { text: '◌ 排队中',   cls: 'bhs-pending'   },
    refunded:    { text: '↩ 已退款',   cls: 'bhs-refunded'  },
  }[item.status] || { text: item.status, cls: '' };

  const place = item.resolved_place_name || '未知地点';
  const modeLabel = item.mode === 'random' ? 'Brew A · 随机' : 'Brew B · 精准';
  const isPlayable = item.status === 'approved' && item.cnu_id;

  return (
    <div className={`brew-hist-card ${item.status}`}>
      <div className="bhc-top">
        <span className={`bhc-status ${statusLabel.cls}`}>{statusLabel.text}</span>
        <span className="bhc-mode">{modeLabel}</span>
      </div>
      <div className="bhc-place">{place}</div>
      {item.story_title && (
        <div className="bhc-title">「{item.story_title}」</div>
      )}
      <div className="bhc-foot">
        <span className="bhc-cost">−{item.credits_deducted}⬡</span>
        {isPlayable && (
          <button className="bhc-play" onClick={() => onPlay && onPlay(item)}
            aria-label="收听">
            <svg viewBox="0 0 24 24" width="10" height="10">
              <path d="M7 5 L19 12 L7 19 Z" fill="currentColor" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ── TierBadge ─────────────────────────────────────────────────────────────────
function TierBadge({ tier }) {
  const map = {
    F:  { label: 'Free',    cls: 'tier-f'  },
    I:  { label: 'Invited', cls: 'tier-i'  },
    P1: { label: 'Pro',     cls: 'tier-p1' },
    P2: { label: 'Creator', cls: 'tier-p2' },
    X:  { label: 'Expert',  cls: 'tier-x'  },
  };
  const t = map[tier] || map['F'];
  return <span className={`brew-tier-badge ${t.cls}`}>{t.label}</span>;
}

// ── BrewPanel (main export) ───────────────────────────────────────────────────
function BrewPanel({ onClose, onStartBrew, userTier = 'F' }) {
  const [credits, setCredits]   = useSt(null);
  const [history, setHistory]   = useSt([]);
  const [histLoading, setHL]    = useSt(true);
  const [credLoading, setCL]    = useSt(true);
  const [credError, setCE]      = useSt(false);

  // Compute days-until-refresh from last_refresh_at (monthly cadence)
  const daysUntilRefresh = useMm(() => {
    if (!credits?.last_refresh_at) return null;
    const last    = new Date(credits.last_refresh_at);
    const nextRef = new Date(last);
    nextRef.setMonth(nextRef.getMonth() + 1);
    const diff = Math.ceil((nextRef - Date.now()) / 86_400_000);
    return Math.max(0, diff);
  }, [credits]);

  // Load credits + history in parallel
  useEff(() => {
    BrewCreditsCache.get()
      .then(c => { setCredits(c); setCL(false); })
      .catch(() => { setCL(false); setCE(true); });

    BrewAPI.getHistory({ pageSize: 6 })
      .then(data => {
        const arr = Array.isArray(data) ? data : (data?.items ?? []);
        setHistory(arr);
        setHL(false);
      })
      .catch(() => setHL(false));
  }, []);

  const canBrewB   = ['I','P1','P2','X'].includes(userTier);
  const isCreator  = userTier === 'P2' || userTier === 'X';
  const balance    = credits?.balance  ?? 0;
  const monthly    = credits?.monthly  ?? 0;

  function handleStartBrew(mode) {
    if (balance < 1 && userTier !== 'X') {
      // Surface insufficient-credits state to parent for modal
      onStartBrew && onStartBrew({ mode, insufficientCredits: true });
      return;
    }
    onStartBrew && onStartBrew({ mode });
  }

  return (
    <div className="brew-panel" role="dialog" aria-modal="true" aria-label="Brew 故事铸造工坊">
      <div className="brew-panel__inner">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="brew-panel__header">
          <div className="brew-panel__title-row">
            <BrewIcon size={20} active={true} />
            <span className="brew-panel__title">BREW</span>
            <TierBadge tier={userTier} />
          </div>
          <p className="brew-panel__sub">你的故事铸造工坊</p>
          <button className="brew-panel__close" onClick={onClose} aria-label="关闭">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
              <path d="M5 5 L19 19 M19 5 L5 19" stroke="currentColor"
                strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Mode cards ─────────────────────────────────────────────────── */}
        <div className="brew-modes">

          {/* Brew A — Random */}
          <div className="brew-mode-card brew-mode-a">
            <div className="bmc-icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#c8973a" strokeWidth="1.2"
                  strokeDasharray="3 2" opacity="0.5" />
                <path d="M7 12 Q10 7 12 12 Q14 17 17 12" stroke="#c8973a"
                  strokeWidth="1.4" strokeLinecap="round" fill="none" />
                <circle cx="12" cy="12" r="1.5" fill="#c8973a" />
              </svg>
            </div>
            <div className="bmc-labels">
              <div className="bmc-name">随机探索</div>
              <div className="bmc-eng">Brew A</div>
              <div className="bmc-desc">系统发现一个<br/>值得讲述的地球故事</div>
            </div>
            <button
              className={`bmc-btn ${balance < 1 && userTier !== 'X' ? 'bmc-btn--disabled' : ''}`}
              onClick={() => handleStartBrew('random')}
              disabled={false /* show but handle in modal */}
            >
              <BrewIcon size={11} color="#08100c" active={false} />
              立即酿造
              <span className="bmc-cost">1⬡</span>
            </button>
          </div>

          {/* Brew B — Coordinate */}
          <div className={`brew-mode-card brew-mode-b ${!canBrewB ? 'brew-mode-locked' : ''}`}>
            <div className="bmc-icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
                <circle cx="12" cy="10" r="4.5" stroke={canBrewB ? '#c8973a' : '#445'} strokeWidth="1.2"/>
                <path d="M12 14.5 Q7 18 12 22 Q17 18 12 14.5" stroke={canBrewB ? '#c8973a' : '#445'}
                  strokeWidth="1.2" strokeLinecap="round" fill="none"/>
                <circle cx="12" cy="10" r="1.4" fill={canBrewB ? '#c8973a' : '#445'} />
              </svg>
            </div>
            <div className="bmc-labels">
              <div className="bmc-name" style={{ color: canBrewB ? undefined : '#556' }}>精准坐标</div>
              <div className="bmc-eng">Brew B</div>
              <div className="bmc-desc" style={{ color: canBrewB ? undefined : '#445' }}>
                {canBrewB
                  ? '你指定，世界响应'
                  : '需要 Invited 或以上层级'}
              </div>
            </div>
            {canBrewB ? (
              <button
                className="bmc-btn"
                onClick={() => handleStartBrew('coordinate')}
              >
                <svg viewBox="0 0 24 24" width="11" height="11" fill="none">
                  <circle cx="12" cy="12" r="8" stroke="#08100c" strokeWidth="1.5"/>
                  <path d="M12 4 V20 M4 12 H20" stroke="#08100c" strokeWidth="1.2"/>
                </svg>
                选择坐标
                <span className="bmc-cost">1⬡</span>
              </button>
            ) : (
              <button className="bmc-btn bmc-btn--upgrade" onClick={() => onStartBrew && onStartBrew({ upgradePrompt: true })}>
                升级解锁
                <svg viewBox="0 0 16 16" width="10" height="10" fill="none">
                  <path d="M8 2 L14 8 M8 2 L2 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                  <path d="M8 2 L8 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ── Credits block ───────────────────────────────────────────────── */}
        <div className="brew-credits-block">
          <div className="brew-credits-head">
            <span>我的酿造点数</span>
            <button className="brew-credits-info" onClick={() => onStartBrew && onStartBrew({ creditsDetail: true })}
              aria-label="点数详情">
              <svg viewBox="0 0 16 16" width="13" height="13" fill="none">
                <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.1"/>
                <path d="M8 7 V11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                <circle cx="8" cy="5" r="0.7" fill="currentColor"/>
              </svg>
            </button>
          </div>

          {credLoading ? (
            <div className="brew-credits-loading">
              <div className="brew-sk-arc"></div>
            </div>
          ) : credError ? (
            <div className="brew-credits-err">点数加载失败 · <button onClick={() => {
              setCL(true); setCE(false);
              BrewCreditsCache.invalidate();
              BrewCreditsCache.get().then(c => { setCredits(c); setCL(false); }).catch(() => { setCL(false); setCE(true); });
            }}>重试</button></div>
          ) : (
            <>
              <CreditsArc
                balance={balance}
                monthly={monthly}
                tier={userTier}
                daysUntilRefresh={daysUntilRefresh}
              />
              {userTier !== 'X' && balance < 3 && (
                <div className="brew-low-credits-hint">
                  <span>点数即将耗尽</span>
                  <button className="brew-get-more" onClick={() => onStartBrew && onStartBrew({ getCredits: true })}>
                    + 获取更多点数
                  </button>
                </div>
              )}
              {userTier === 'X' && (
                <div className="brew-unlimited-badge">∞ Expert 无限点数</div>
              )}
            </>
          )}
        </div>

        {/* ── History strip ───────────────────────────────────────────────── */}
        <div className="brew-history-block">
          <div className="brew-history-head">
            <span>我的酿造记录</span>
            <button className="brew-history-all" onClick={() => onStartBrew && onStartBrew({ viewHistory: true })}>
              查看全部 →
            </button>
          </div>

          {histLoading ? (
            <div className="brew-hist-loading">
              {[1,2,3].map(i => <div key={i} className="brew-hist-sk"></div>)}
            </div>
          ) : history.length === 0 ? (
            <div className="brew-hist-empty">
              <div className="bhe-icon">
                <BrewIcon size={22} color="rgba(200,151,58,0.3)" />
              </div>
              <div className="bhe-text">还没有酿造记录</div>
              <div className="bhe-sub">点击上方按钮开始第一次探索</div>
            </div>
          ) : (
            <div className="brew-hist-scroll">
              {history.map(item => (
                <BrewHistoryCard key={item.id} item={item}
                  onPlay={(it) => onStartBrew && onStartBrew({ playBrew: it })} />
              ))}
            </div>
          )}
        </div>

        {/* ── Creator/Expert analytics teaser ────────────────────────────── */}
        {isCreator && (
          <div className="brew-creator-teaser">
            <div className="bct-label">Creator 数据洞察</div>
            <div className="bct-stats">
              <div className="bct-stat">
                <span className="bcts-n">—</span>
                <span className="bcts-l">总播放</span>
              </div>
              <div className="bct-stat">
                <span className="bcts-n">—</span>
                <span className="bcts-l">通过率</span>
              </div>
              <div className="bct-stat">
                <span className="bcts-n">—</span>
                <span className="bcts-l">收藏量</span>
              </div>
            </div>
            <div className="bct-coming">Analytics · 即将上线</div>
          </div>
        )}

        {/* ── Footer legal nudge ──────────────────────────────────────────── */}
        <div className="brew-panel__foot">
          酿造内容经 AI 辅助生成 · 经审核后可进入公共内容池
        </div>

      </div>
    </div>
  );
}

window.BrewPanel = BrewPanel;
window.BrewIcon = BrewIcon;
